// Ring-buffered capture of the things you wish you had when a bug comes in:
// console output, failed/slow requests, unhandled errors, and the environment.
//
// Everything is bounded — a long-lived page must not grow memory without limit.

import { redactUrl, redactText } from './redact.js';

export function createRingBuffer(limit) {
  const items = [];
  return {
    push(item) {
      items.push(item);
      if (items.length > limit) items.splice(0, items.length - limit);
    },
    all: () => items.slice(),
    clear: () => { items.length = 0; },
    get size() { return items.length; },
  };
}

const LEVELS = ['log', 'info', 'warn', 'error', 'debug'];

/** Wraps console.* and records what goes through, then calls the original. */
export function installConsoleCapture(buffer, { levels = ['warn', 'error', 'log', 'info'] } = {}) {
  const originals = {};
  for (const level of LEVELS) {
    originals[level] = console[level];
    if (!levels.includes(level)) continue;
    console[level] = (...args) => {
      try {
        buffer.push({
          level,
          text: args.map(stringify).join(' ').slice(0, 2000),
          at: new Date().toISOString(),
        });
      } catch { /* capture must never break the page */ }
      originals[level].apply(console, args);
    };
  }
  return () => { for (const level of LEVELS) console[level] = originals[level]; };
}

/**
 * Records fetch + XHR: url, method, status, duration. Bodies are never read,
 * which leaves the URL as the only place a credential can arrive — so every
 * entry goes through redact.js on the way into the buffer.
 */
export function installNetworkCapture(buffer, { slowMs = 2000 } = {}) {
  const originalFetch = window.fetch;
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  if (originalFetch) {
    window.fetch = async function patchedFetch(...args) {
      const started = performance.now();
      const url = typeof args[0] === 'string' ? args[0] : args[0]?.url;
      const method = args[1]?.method || (typeof args[0] === 'object' ? args[0]?.method : 'GET') || 'GET';
      try {
        const res = await originalFetch.apply(this, args);
        record(buffer, { url, method, status: res.status, ms: Math.round(performance.now() - started), slowMs });
        return res;
      } catch (err) {
        record(buffer, { url, method, status: 0, error: String(err?.message || err), ms: Math.round(performance.now() - started), slowMs });
        throw err;
      }
    };
  }

  XMLHttpRequest.prototype.open = function patchedOpen(method, url, ...rest) {
    this.__bc = { method, url, started: 0 };
    return originalOpen.call(this, method, url, ...rest);
  };
  XMLHttpRequest.prototype.send = function patchedSend(...args) {
    if (this.__bc) {
      this.__bc.started = performance.now();
      this.addEventListener('loadend', () => {
        record(buffer, {
          url: this.__bc.url,
          method: this.__bc.method,
          status: this.status,
          ms: Math.round(performance.now() - this.__bc.started),
          slowMs,
        });
      });
    }
    return originalSend.apply(this, args);
  };

  return () => {
    if (originalFetch) window.fetch = originalFetch;
    XMLHttpRequest.prototype.open = originalOpen;
    XMLHttpRequest.prototype.send = originalSend;
  };
}

function record(buffer, { url, method, status, ms, error, slowMs }) {
  try {
    buffer.push({
      // Scrubbed before it is buffered, not before it is sent: a credential
      // that never enters the ring buffer cannot leave with a report, and
      // cannot be read back out of memory by the page either. Redact first,
      // then truncate — truncating first would split a token into something
      // no pattern recognises, and the fragment would still be a fragment of
      // a live credential.
      url: redactUrl(String(url)).slice(0, 500),
      method,
      status,
      ms,
      error: error === undefined ? undefined : redactText(error),
      // Flagged so triage can spot the interesting rows without reading everything.
      failed: status === 0 || status >= 400,
      slow: ms >= slowMs,
      at: new Date().toISOString(),
    });
  } catch { /* ignore */ }
}

/** Unhandled errors and rejections, appended to the console buffer. */
export function installErrorCapture(buffer) {
  const onError = (event) => {
    // Browsers already prefix event.message with "Uncaught" — don't say it twice.
    const message = String(event.message || 'Error');
    const where = event.filename ? ` (${event.filename}:${event.lineno}:${event.colno})` : '';
    buffer.push({
      level: 'error',
      text: (/^uncaught/i.test(message) ? message : `Uncaught ${message}`) + where,
      at: new Date().toISOString(),
    });
  };
  const onRejection = (event) => {
    buffer.push({
      level: 'error',
      text: `Unhandled rejection: ${stringify(event.reason)}`,
      at: new Date().toISOString(),
    });
  };
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}

/**
 * Battery needs the async Battery Status API, so environment collection is
 * async now. Unsupported browsers (Safari, Firefox) simply omit the fields.
 */
export async function collectEnvironment() {
  const base = collectEnvironmentSync();
  try {
    if (navigator.getBattery) {
      const b = await navigator.getBattery();
      base.battery = Math.round(b.level * 100);
      base.charging = b.charging;
    }
  } catch { /* not available — leave the fields out */ }
  return base;
}

export function collectEnvironmentSync() {
  const nav = window.navigator || {};
  return {
    userAgent: nav.userAgent,
    platform: nav.platform,
    language: nav.language,
    languages: nav.languages ? [...nav.languages] : null,
    online: nav.onLine,
    cookieEnabled: nav.cookieEnabled,
    maxTouchPoints: nav.maxTouchPoints ?? null,
    viewport: { width: window.innerWidth, height: window.innerHeight },
    screen: {
      width: window.screen?.width,
      height: window.screen?.height,
      availWidth: window.screen?.availWidth,
      availHeight: window.screen?.availHeight,
      colorDepth: window.screen?.colorDepth,
      orientation: window.screen?.orientation?.type || null,
    },
    devicePixelRatio: window.devicePixelRatio,
    referrer: document.referrer || null,
    title: document.title,
    capturedAt: new Date().toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffsetMinutes: new Date().getTimezoneOffset(),
    // A hint, not a guarantee: `webdriver` is the only broadly-available signal
    // that something other than a person is driving the page.
    browserMode: nav.webdriver ? 'Automated' : 'Normal',
    doNotTrack: nav.doNotTrack || null,
    hardwareConcurrency: nav.hardwareConcurrency ?? null,
    deviceMemory: nav.deviceMemory ?? null,
    connection: nav.connection
      ? {
          effectiveType: nav.connection.effectiveType,
          downlink: nav.connection.downlink,
          rtt: nav.connection.rtt,
          saveData: nav.connection.saveData,
        }
      : null,
    prefersColorScheme: window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light',
    prefersReducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || false,
  };
}

function stringify(value) {
  if (typeof value === 'string') return value;
  if (value instanceof Error) return `${value.name}: ${value.message}`;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}
